import { SUBLIMINAL_GAIN_CEILING } from "./subliminal-level.js";

export { SUBLIMINAL_GAIN_CEILING } from "./subliminal-level.js";

export const DEFAULT_BRAINWAVE_LAB = Object.freeze({
  enabled: false,
  toneEnabled: false,
  baseFrequencyHz: 180,
  beatHz: 6,
  // Level of the AUDIBLE binaural carrier (the pair of sine oscillators in
  // audio-deck-controller.js). This is the layer that actually entrains, so it
  // is exposed rather than hardcoded. Range is deliberately narrow: below ~0.02
  // the carrier disappears under the music bed (-34 dB vs a 0.68 bed) and
  // entrains nobody — see the Entrainment Floor law. 0.05 is ~-26 dB: clearly
  // present, still well below the voice. Ceiling 0.12 keeps it subordinate.
  binauralLevel: 0.05,
  // Experimental, explicitly enabled beat-rate trajectory. A slow bounded
  // drift is easier to measure than abrupt beat steps; it is not a claim of
  // neural entrainment.
  beatRateTrajectoryEnabled: true,
  beatRateTrajectoryDepthHz: 0.75,
  beatRateTrajectoryPeriodSeconds: 45,
  waveform: "sine",
  modulationDepth: 0.12,
  subliminalRippleDepth: 0.05,
  volume: 0.06,
  phaseDegrees: 0,
  stereoDelayMs: 0,
  // Speech remains the priority. Keep the effect as a short, low-level
  // spatial tail rather than a repeating audible echo.
  echoDelayMs: 130,
  echoFeedback: 0.025,
  echoMix: 0.035,
  // Keep masked speech in the audible band by default. A fourteen-kilohertz
  // relocation is fragile across Bluetooth codecs, speakers, and hearing
  // ranges and can reduce the lane to no delivered linguistic signal at all.
  carrierShiftHz: 0,
  breathCycleSeconds: 7.5,
  breathInSeconds: 3.5,
  breathOutSeconds: 4,
  breathDepth: 0.16,
  breathSoundEnabled: true,
  breathSoundLevel: 0.014,
  breathAirColor: "soft",
  crossfeed: 0.35,
  highpassHz: 240,
  lowpassHz: 2000,
  subliminalGain: 0.8,
  backgroundGain: 0.8,
  bassRippleEnabled: true,
  bassRippleDepth: 0.08,
  bassPulseBpm: 36,
  // Envelope-following entrainment: the source's own rhythm (e.g. moans/cries)
  // drives the ripple/bulge instead of a fixed LFO. 0 = fixed, 1 = source.
  envelopeEntrainment: 0,
  envelopeAttackMs: 12,
  envelopeReleaseMs: 220,
  // Covert masking: slow bounded bulge on the common carrier so the content
  // breathes at the perceptual edge of adult hearing. 0 = off.
  covertMaskDepth: 0,
  covertMaskHz: 0.15,
  // Phase coherence (Black Book §5.5): lock the bass pulse and covert mask to
  // the master binaural beat phase so all layers crest together, reinforcing a
  // single frequency-following response. 0 = independent, 1 = fully locked.
  phaseCoherence: 1,
  heartbeatEnabled: true,
  heartbeatLevel: 0.018,
  heartbeatCarrierHz: 70,
  heartbeatDoubletMs: 220,
  heartbeatHarmonicRatio: 0.32,
  binauralEnabled: true,
  subliminalEnabled: true,
  spatialEnabled: true,
  breathEnabled: true,
  echoEnabled: true,
  filtersEnabled: true,
  mixEnabled: true,
  polytemporalEnabled: true,
  polytemporalLevel: 0.012,
  polytemporalDepth: 0.30,
  polytemporalDirectAmEnabled: true,
  polytemporalDirectAmDepth: 0.30,
  polytemporalHarmonicEnabled: true,
  polytemporalHarmonicDepth: 0.16,
  polytemporalCrossFrequencyEnabled: true,
  polytemporalCrossFrequencyHz: 40,
  polytemporalCrossFrequencyDepth: 0.07,
  polytemporalSpatialWidth: 0.55,
  polytemporalTransientLevel: 0.12,
  polytemporalJitterPercent: 0.035,
  polytemporalCarrierProfile: "balanced",
  polytemporalPulseClarity: "clear",
  polytemporalComfort: "balanced",
  polytemporalMovement: "subtle",
  polytemporalRegularity: "organic",
  polytemporalContinuousDeepening: true,
  polytemporalDeepeningCycleSeconds: 360,
  polytemporalDescentBias: 0.82,
  // Director, 2026-08-18: deepen the felt pulse through the brainwave bands
  // (theta -> alpha -> beta -> low gamma) toward erotic dreamstates.
  polytemporalBandArcEnabled: true,
  polytemporalBandArc: [
    [0, 6],
    [120, 6],
    [360, 8],
    [840, 10],
    [1800, 14],
    [3600, 20],
    [7200, 30],
  ],
  // ── Target depth / type (Black Book §5.5) ────────────────────────────────
  // A settable destination the whole system drives toward over time. "Depth"
  // is a named trance state that maps to a target brainwave band; the system
  // glides from the current band down toward it (progressive descent) so depth
  // increases continuously. "Type" is the flavor of the trance that adjusts
  // the band arc direction.
  //   depth: "light" (alpha 8-12) | "medium" (theta 6-8) | "deep" (theta 4-6)
  //          | "ultra" (delta 2-4) | "auto" (glide all the way to delta)
  //   type:  "depth" (pure descent, theta->delta) | "dream" (erotic dreamstate,
  //          theta->alpha->beta->gamma arousal arc) | "auto" (follow depth)
  targetDepth: "auto",
  targetType: "auto",
  // How long (seconds) the progressive descent takes to reach the target band.
  targetDescentSeconds: 900,
  // Whether the target is a hard ceiling (stop at target) or a soft pull
  // (keep deepening slowly past it). "auto" depth uses soft.
  targetDepthHardCeiling: false,
  // The band arc is derived from targetDepth/targetType at scene-build time.
  // This is the resolved arc (overrides polytemporalBandArc when set).
  derivedBandArc: null,
  // ── Arousal pulse (NSFW) ─────────────────────────────────────────────────
  // The arousal-trance loop (Black Book §5.5): pulse the brainwave target UP
  // into the arousal band (beta/gamma), then DROP it into deep trance
  // (theta/delta). Each arousal peak makes the following descent land harder —
  // the contrast is what deepens. This is neural fractionation.
  //   arousalPulseEnabled: master on/off (NSFW sessions only)
  //   arousalPulseHz:      the arousal peak frequency (beta 14-30 / gamma 30-60)
  //   arousalPulseDepthHz: how far below the peak the drop goes (deep theta/delta)
  //   arousalPulseRiseS:   seconds to climb to the peak
  //   arousalPulseHoldS:   seconds to hold the peak
  //   arousalPulseDropS:   seconds to fall into the deep trance
  //   arousalPulseRestS:   seconds to rest in deep trance before the next pulse
  arousalPulseEnabled: false,
  arousalPulseHz: 24,
  arousalPulseDepthHz: 3,
  arousalPulseRiseS: 20,
  arousalPulseHoldS: 12,
  arousalPulseDropS: 30,
  arousalPulseRestS: 60,
  spatialSpectacleEnabled: true,
  spatialSpectacleIntensity: 0.42,
  spatialSpiralEnabled: true,
  spatialSpiralSeconds: 32,
  spatialSpiralRadius: 0.72,
  spatialSpiralDescent: 0.58,
  spatialStaircaseEnabled: true,
  spatialStaircaseLevel: 0.007,
  spatialStaircaseSeconds: 28,
  spatialVortexEnabled: true,
  spatialVortexLevel: 0.005,
  spatialBreathRoomDepth: 0.34,
  spatialTrapdoorEnabled: true,
  spatialTrapdoorLevel: 0.012,
  spatialTrapdoorIntervalSeconds: 210,
});

// A universal perceptual threshold cannot be guaranteed across devices,
// Bluetooth codecs, speaker levels, or listeners. This is an engineering mix
// ceiling, not a claim about a human perceptual threshold. Keep the lane well
// below full-scale and use masking/spectral shaping instead of extra level.
// 0.02 is approximately -34 dBFS when the source is normalized to full-scale.
const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
};

export function normalizeBrainwaveLab(value = {}) {
  const waveform = ["sine", "triangle", "square", "sawtooth"].includes(String(value.waveform))
    ? String(value.waveform)
    : DEFAULT_BRAINWAVE_LAB.waveform;
  const legacyBreathHalf = clamp(
    Number(value.breathCycleSeconds) / 2, 2.5, 6,
    DEFAULT_BRAINWAVE_LAB.breathCycleSeconds / 2,
  );
  const breathInSeconds = clamp(
    value.breathInSeconds, 2.5, 5,
    Number.isFinite(Number(value.breathCycleSeconds)) ? legacyBreathHalf : DEFAULT_BRAINWAVE_LAB.breathInSeconds,
  );
  const breathOutSeconds = clamp(
    value.breathOutSeconds, 2.5, 6,
    Number.isFinite(Number(value.breathCycleSeconds)) ? legacyBreathHalf : DEFAULT_BRAINWAVE_LAB.breathOutSeconds,
  );
  return {
    enabled: Boolean(value.enabled),
    toneEnabled: Boolean(value.toneEnabled),
    baseFrequencyHz: clamp(value.baseFrequencyHz, 40, 1200, DEFAULT_BRAINWAVE_LAB.baseFrequencyHz),
    binauralLevel: clamp(value.binauralLevel, 0, 0.12, DEFAULT_BRAINWAVE_LAB.binauralLevel),
    beatHz: clamp(value.beatHz, 0.5, 40, DEFAULT_BRAINWAVE_LAB.beatHz),
    beatRateTrajectoryEnabled: value.beatRateTrajectoryEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.beatRateTrajectoryEnabled : Boolean(value.beatRateTrajectoryEnabled),
    beatRateTrajectoryDepthHz: clamp(value.beatRateTrajectoryDepthHz, 0, 5, DEFAULT_BRAINWAVE_LAB.beatRateTrajectoryDepthHz),
    beatRateTrajectoryPeriodSeconds: clamp(value.beatRateTrajectoryPeriodSeconds, 30, 60, DEFAULT_BRAINWAVE_LAB.beatRateTrajectoryPeriodSeconds),
    waveform,
    modulationDepth: clamp(value.modulationDepth, 0, 0.9, DEFAULT_BRAINWAVE_LAB.modulationDepth),
    subliminalRippleDepth: clamp(value.subliminalRippleDepth, 0, 0.18, DEFAULT_BRAINWAVE_LAB.subliminalRippleDepth),
    volume: clamp(value.volume, 0, 0.25, DEFAULT_BRAINWAVE_LAB.volume),
    phaseDegrees: clamp(value.phaseDegrees, -180, 180, DEFAULT_BRAINWAVE_LAB.phaseDegrees),
    stereoDelayMs: clamp(value.stereoDelayMs, 0, 30, DEFAULT_BRAINWAVE_LAB.stereoDelayMs),
    echoDelayMs: clamp(value.echoDelayMs, 0, 750, DEFAULT_BRAINWAVE_LAB.echoDelayMs),
    echoFeedback: clamp(value.echoFeedback, 0, 0.65, DEFAULT_BRAINWAVE_LAB.echoFeedback),
    echoMix: clamp(value.echoMix, 0, 0.5, DEFAULT_BRAINWAVE_LAB.echoMix),
    carrierShiftHz: clamp(value.carrierShiftHz, -2000, 16000, DEFAULT_BRAINWAVE_LAB.carrierShiftHz),
    breathCycleSeconds: breathInSeconds + breathOutSeconds,
    breathInSeconds,
    breathOutSeconds,
    breathDepth: clamp(value.breathDepth, 0, 0.5, DEFAULT_BRAINWAVE_LAB.breathDepth),
    breathSoundEnabled: value.breathSoundEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.breathSoundEnabled : Boolean(value.breathSoundEnabled),
    breathSoundLevel: clamp(value.breathSoundLevel, 0, 0.03, DEFAULT_BRAINWAVE_LAB.breathSoundLevel),
    breathAirColor: ["soft", "balanced", "airy"].includes(String(value.breathAirColor))
      ? String(value.breathAirColor) : DEFAULT_BRAINWAVE_LAB.breathAirColor,
    crossfeed: clamp(value.crossfeed, 0, 0.45, DEFAULT_BRAINWAVE_LAB.crossfeed),
    highpassHz: clamp(value.highpassHz, 20, 1200, DEFAULT_BRAINWAVE_LAB.highpassHz),
    lowpassHz: clamp(value.lowpassHz, 1200, 18000, DEFAULT_BRAINWAVE_LAB.lowpassHz),
    subliminalGain: clamp(value.subliminalGain, 0, 1, DEFAULT_BRAINWAVE_LAB.subliminalGain),
    backgroundGain: clamp(value.backgroundGain, 0, 1, DEFAULT_BRAINWAVE_LAB.backgroundGain),
    bassRippleEnabled: value.bassRippleEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.bassRippleEnabled : Boolean(value.bassRippleEnabled),
    bassRippleDepth: clamp(value.bassRippleDepth, 0, 0.3, DEFAULT_BRAINWAVE_LAB.bassRippleDepth),
    bassPulseBpm: clamp(value.bassPulseBpm, 18, 120, DEFAULT_BRAINWAVE_LAB.bassPulseBpm),
    envelopeEntrainment: clamp(value.envelopeEntrainment, 0, 1, DEFAULT_BRAINWAVE_LAB.envelopeEntrainment),
    envelopeAttackMs: clamp(value.envelopeAttackMs, 1, 200, DEFAULT_BRAINWAVE_LAB.envelopeAttackMs),
    envelopeReleaseMs: clamp(value.envelopeReleaseMs, 20, 2000, DEFAULT_BRAINWAVE_LAB.envelopeReleaseMs),
    covertMaskDepth: clamp(value.covertMaskDepth, 0, 0.5, DEFAULT_BRAINWAVE_LAB.covertMaskDepth),
    covertMaskHz: clamp(value.covertMaskHz, 0.05, 0.5, DEFAULT_BRAINWAVE_LAB.covertMaskHz),
    phaseCoherence: clamp(value.phaseCoherence, 0, 1, DEFAULT_BRAINWAVE_LAB.phaseCoherence),
    heartbeatEnabled: value.heartbeatEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.heartbeatEnabled : Boolean(value.heartbeatEnabled),
    heartbeatLevel: clamp(value.heartbeatLevel, 0, 0.05, DEFAULT_BRAINWAVE_LAB.heartbeatLevel),
    heartbeatCarrierHz: clamp(value.heartbeatCarrierHz, 36, 96, DEFAULT_BRAINWAVE_LAB.heartbeatCarrierHz),
    heartbeatDoubletMs: clamp(value.heartbeatDoubletMs, 110, 260, DEFAULT_BRAINWAVE_LAB.heartbeatDoubletMs),
    heartbeatHarmonicRatio: clamp(value.heartbeatHarmonicRatio, 0, 0.6, DEFAULT_BRAINWAVE_LAB.heartbeatHarmonicRatio),
    binauralEnabled: value.binauralEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.binauralEnabled : Boolean(value.binauralEnabled),
    subliminalEnabled: value.subliminalEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.subliminalEnabled : Boolean(value.subliminalEnabled),
    spatialEnabled: value.spatialEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialEnabled : Boolean(value.spatialEnabled),
    breathEnabled: value.breathEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.breathEnabled : Boolean(value.breathEnabled),
    echoEnabled: value.echoEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.echoEnabled : Boolean(value.echoEnabled),
    filtersEnabled: value.filtersEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.filtersEnabled : Boolean(value.filtersEnabled),
    mixEnabled: value.mixEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.mixEnabled : Boolean(value.mixEnabled),
    polytemporalEnabled: value.polytemporalEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalEnabled
      : Boolean(value.polytemporalEnabled),
    polytemporalLevel: clamp(value.polytemporalLevel, 0, 0.04, DEFAULT_BRAINWAVE_LAB.polytemporalLevel),
    polytemporalDepth: clamp(value.polytemporalDepth, 0.08, 0.55, DEFAULT_BRAINWAVE_LAB.polytemporalDepth),
    polytemporalDirectAmEnabled: value.polytemporalDirectAmEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalDirectAmEnabled : Boolean(value.polytemporalDirectAmEnabled),
    polytemporalDirectAmDepth: clamp(value.polytemporalDirectAmDepth, 0, 0.55, DEFAULT_BRAINWAVE_LAB.polytemporalDirectAmDepth),
    polytemporalHarmonicEnabled: value.polytemporalHarmonicEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalHarmonicEnabled : Boolean(value.polytemporalHarmonicEnabled),
    polytemporalHarmonicDepth: clamp(value.polytemporalHarmonicDepth, 0, 0.22, DEFAULT_BRAINWAVE_LAB.polytemporalHarmonicDepth),
    polytemporalCrossFrequencyEnabled: value.polytemporalCrossFrequencyEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalCrossFrequencyEnabled : Boolean(value.polytemporalCrossFrequencyEnabled),
    polytemporalCrossFrequencyHz: clamp(value.polytemporalCrossFrequencyHz, 30, 45, DEFAULT_BRAINWAVE_LAB.polytemporalCrossFrequencyHz),
    polytemporalCrossFrequencyDepth: clamp(value.polytemporalCrossFrequencyDepth, 0, 0.10, DEFAULT_BRAINWAVE_LAB.polytemporalCrossFrequencyDepth),
    polytemporalSpatialWidth: clamp(value.polytemporalSpatialWidth, 0, 1, DEFAULT_BRAINWAVE_LAB.polytemporalSpatialWidth),
    polytemporalTransientLevel: clamp(value.polytemporalTransientLevel, 0, 0.24, DEFAULT_BRAINWAVE_LAB.polytemporalTransientLevel),
    polytemporalJitterPercent: clamp(value.polytemporalJitterPercent, 0.02, 0.05, DEFAULT_BRAINWAVE_LAB.polytemporalJitterPercent),
    polytemporalCarrierProfile: ["soft", "balanced", "bright"].includes(String(value.polytemporalCarrierProfile))
      ? String(value.polytemporalCarrierProfile) : DEFAULT_BRAINWAVE_LAB.polytemporalCarrierProfile,
    polytemporalPulseClarity: ["soft", "clear"].includes(String(value.polytemporalPulseClarity))
      ? String(value.polytemporalPulseClarity) : DEFAULT_BRAINWAVE_LAB.polytemporalPulseClarity,
    polytemporalComfort: ["sensitive", "balanced", "strong"].includes(String(value.polytemporalComfort))
      ? String(value.polytemporalComfort) : DEFAULT_BRAINWAVE_LAB.polytemporalComfort,
    polytemporalMovement: ["still", "subtle", "wide"].includes(String(value.polytemporalMovement))
      ? String(value.polytemporalMovement) : DEFAULT_BRAINWAVE_LAB.polytemporalMovement,
    polytemporalRegularity: ["steady", "organic"].includes(String(value.polytemporalRegularity))
      ? String(value.polytemporalRegularity) : DEFAULT_BRAINWAVE_LAB.polytemporalRegularity,
    polytemporalContinuousDeepening: value.polytemporalContinuousDeepening === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalContinuousDeepening
      : Boolean(value.polytemporalContinuousDeepening),
    polytemporalDeepeningCycleSeconds: clamp(
      value.polytemporalDeepeningCycleSeconds, 180, 900,
      DEFAULT_BRAINWAVE_LAB.polytemporalDeepeningCycleSeconds,
    ),
    polytemporalDescentBias: clamp(
      value.polytemporalDescentBias, 0, 1,
      DEFAULT_BRAINWAVE_LAB.polytemporalDescentBias,
    ),
    polytemporalBandArcEnabled: value.polytemporalBandArcEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.polytemporalBandArcEnabled
      : Boolean(value.polytemporalBandArcEnabled),
    polytemporalBandArc: Array.isArray(value.polytemporalBandArc) && value.polytemporalBandArc.length >= 2
      ? value.polytemporalBandArc
        .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
        .map((p) => [Number(p[0]), Number(p[1])])
        .sort((a, b) => a[0] - b[0])
      : DEFAULT_BRAINWAVE_LAB.polytemporalBandArc,
    targetDepth: ["light", "medium", "deep", "ultra", "auto"].includes(String(value.targetDepth))
      ? String(value.targetDepth) : DEFAULT_BRAINWAVE_LAB.targetDepth,
    targetType: ["depth", "dream", "auto"].includes(String(value.targetType))
      ? String(value.targetType) : DEFAULT_BRAINWAVE_LAB.targetType,
    targetDescentSeconds: clamp(value.targetDescentSeconds, 120, 3600, DEFAULT_BRAINWAVE_LAB.targetDescentSeconds),
    targetDepthHardCeiling: value.targetDepthHardCeiling === undefined
      ? DEFAULT_BRAINWAVE_LAB.targetDepthHardCeiling : Boolean(value.targetDepthHardCeiling),
    derivedBandArc: Array.isArray(value.derivedBandArc) && value.derivedBandArc.length >= 2
      ? value.derivedBandArc
        .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
        .map((p) => [Number(p[0]), Number(p[1])])
        .sort((a, b) => a[0] - b[0])
      : null,
    arousalPulseEnabled: value.arousalPulseEnabled === undefined
      ? DEFAULT_BRAINWAVE_LAB.arousalPulseEnabled : Boolean(value.arousalPulseEnabled),
    arousalPulseHz: clamp(value.arousalPulseHz, 14, 60, DEFAULT_BRAINWAVE_LAB.arousalPulseHz),
    arousalPulseDepthHz: clamp(value.arousalPulseDepthHz, 1, 8, DEFAULT_BRAINWAVE_LAB.arousalPulseDepthHz),
    arousalPulseRiseS: clamp(value.arousalPulseRiseS, 5, 120, DEFAULT_BRAINWAVE_LAB.arousalPulseRiseS),
    arousalPulseHoldS: clamp(value.arousalPulseHoldS, 3, 60, DEFAULT_BRAINWAVE_LAB.arousalPulseHoldS),
    arousalPulseDropS: clamp(value.arousalPulseDropS, 10, 180, DEFAULT_BRAINWAVE_LAB.arousalPulseDropS),
    arousalPulseRestS: clamp(value.arousalPulseRestS, 15, 600, DEFAULT_BRAINWAVE_LAB.arousalPulseRestS),
    spatialSpectacleEnabled: value.spatialSpectacleEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialSpectacleEnabled : Boolean(value.spatialSpectacleEnabled),
    spatialSpectacleIntensity: clamp(value.spatialSpectacleIntensity, 0, 1, DEFAULT_BRAINWAVE_LAB.spatialSpectacleIntensity),
    spatialSpiralEnabled: value.spatialSpiralEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialSpiralEnabled : Boolean(value.spatialSpiralEnabled),
    spatialSpiralSeconds: clamp(value.spatialSpiralSeconds, 16, 90, DEFAULT_BRAINWAVE_LAB.spatialSpiralSeconds),
    spatialSpiralRadius: clamp(value.spatialSpiralRadius, 0.2, 1.4, DEFAULT_BRAINWAVE_LAB.spatialSpiralRadius),
    spatialSpiralDescent: clamp(value.spatialSpiralDescent, 0, 1.2, DEFAULT_BRAINWAVE_LAB.spatialSpiralDescent),
    spatialStaircaseEnabled: value.spatialStaircaseEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialStaircaseEnabled : Boolean(value.spatialStaircaseEnabled),
    spatialStaircaseLevel: clamp(value.spatialStaircaseLevel, 0, 0.018, DEFAULT_BRAINWAVE_LAB.spatialStaircaseLevel),
    spatialStaircaseSeconds: clamp(value.spatialStaircaseSeconds, 16, 60, DEFAULT_BRAINWAVE_LAB.spatialStaircaseSeconds),
    spatialVortexEnabled: value.spatialVortexEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialVortexEnabled : Boolean(value.spatialVortexEnabled),
    spatialVortexLevel: clamp(value.spatialVortexLevel, 0, 0.014, DEFAULT_BRAINWAVE_LAB.spatialVortexLevel),
    spatialBreathRoomDepth: clamp(value.spatialBreathRoomDepth, 0, 0.75, DEFAULT_BRAINWAVE_LAB.spatialBreathRoomDepth),
    spatialTrapdoorEnabled: value.spatialTrapdoorEnabled === undefined ? DEFAULT_BRAINWAVE_LAB.spatialTrapdoorEnabled : Boolean(value.spatialTrapdoorEnabled),
    spatialTrapdoorLevel: clamp(value.spatialTrapdoorLevel, 0, 0.025, DEFAULT_BRAINWAVE_LAB.spatialTrapdoorLevel),
    spatialTrapdoorIntervalSeconds: clamp(value.spatialTrapdoorIntervalSeconds, 90, 600, DEFAULT_BRAINWAVE_LAB.spatialTrapdoorIntervalSeconds),
  };
}

// ── Target depth / type / arousal-pulse resolution (Black Book §5.5) ──────
// Maps a named target depth + type to a concrete band arc (time, Hz) that the
// polytemporal field glides along. "depth" type descends theta->delta (deep
// trance); "dream" type ascends theta->alpha->beta->gamma (erotic dreamstate).
// The arousal pulse overlays a rise->hold->drop->rest cycle on top, so each
// arousal peak is followed by a steeper drop into deep trance (fractionation).

const TARGET_DEPTH_BANDS = {
  light: 10,   // alpha
  medium: 7,   // theta
  deep: 5,     // theta (deep)
  ultra: 3,    // delta
  auto: 2,     // glide all the way to delta
};

export function resolveTargetBandArc(lab = {}) {
  const depth = String(lab.targetDepth || "auto");
  const type = String(lab.targetType || "auto");
  const descentS = Math.max(120, Number(lab.targetDescentSeconds) || 900);
  const hardCeiling = Boolean(lab.targetDepthHardCeiling);
  const base = Number(lab.beatHz) || 6;

  // "dream" type: erotic arousal arc (theta -> alpha -> beta -> gamma).
  if (type === "dream") {
    return [
      [0, base],
      [Math.round(descentS * 0.25), Math.min(12, base + 4)],
      [Math.round(descentS * 0.5), 14],
      [Math.round(descentS * 0.75), 20],
      [descentS, 30],
    ];
  }

  // "depth" type (or auto): pure descent toward the target band.
  const targetHz = TARGET_DEPTH_BANDS[depth] ?? 2;
  const startHz = Math.max(targetHz, base);
  const arc = [
    [0, startHz],
    [Math.round(descentS * 0.5), startHz - (startHz - targetHz) * 0.6],
    [descentS, targetHz],
  ];
  // Soft pull: keep deepening slowly past the target (toward delta).
  if (!hardCeiling && depth !== "ultra") {
    arc.push([descentS + 600, Math.max(1.5, targetHz - 1)]);
  }
  return arc;
}

/**
 * Resolve the instantaneous brainwave target at elapsed time, applying the
 * arousal pulse (rise->hold->drop->rest) on top of the base band arc.
 * Returns { hz, phase } where phase is "rise"|"hold"|"drop"|"rest"|"base".
 */
export function resolveArousalPulseHz(elapsedSeconds = 0, lab = {}) {
  const elapsed = Math.max(0, Number(elapsedSeconds) || 0);
  const baseHz = resolveBandHzFromArc(elapsed, resolveTargetBandArc(lab));
  if (!lab.arousalPulseEnabled) return { hz: baseHz, phase: "base" };

  const peak = Math.max(14, Math.min(60, Number(lab.arousalPulseHz) || 24));
  const depthHz = Math.max(1, Math.min(8, Number(lab.arousalPulseDepthHz) || 3));
  const riseS = Math.max(5, Number(lab.arousalPulseRiseS) || 20);
  const holdS = Math.max(3, Number(lab.arousalPulseHoldS) || 12);
  const dropS = Math.max(10, Number(lab.arousalPulseDropS) || 30);
  const restS = Math.max(15, Number(lab.arousalPulseRestS) || 60);
  const cycle = riseS + holdS + dropS + restS;
  const t = elapsed % cycle;

  // Expose the pulse geometry so the covert engine can deterministically
  // schedule atoms to land on the drop (Black Book §5.6). Both derive from the
  // same session clock, so they cannot drift apart.
  const geometry = { riseS, holdS, dropS, restS, cycle };

  if (t < riseS) {
    // Climb from the deep-trance floor up to the arousal peak.
    const p = t / riseS;
    const s = p * p * (3 - 2 * p); // smoothstep
    return { hz: depthHz + (peak - depthHz) * s, phase: "rise", ...geometry };
  }
  if (t < riseS + holdS) {
    return { hz: peak, phase: "hold", ...geometry };
  }
  if (t < riseS + holdS + dropS) {
    // Drop from the peak down into deep trance (the fractionation descent).
    const p = (t - riseS - holdS) / dropS;
    const s = p * p * (3 - 2 * p);
    return { hz: peak - (peak - depthHz) * s, phase: "drop", ...geometry };
  }
  return { hz: depthHz, phase: "rest", ...geometry };
}

function resolveBandHzFromArc(elapsed, arc) {
  if (!Array.isArray(arc) || arc.length === 0) return 6;
  if (elapsed <= arc[0][0]) return arc[0][1];
  const last = arc[arc.length - 1];
  if (elapsed >= last[0]) return last[1];
  for (let i = 1; i < arc.length; i += 1) {
    const [t0, hz0] = arc[i - 1];
    const [t1, hz1] = arc[i];
    if (elapsed <= t1) {
      const span = Math.max(1, t1 - t0);
      const t = (elapsed - t0) / span;
      const s = t * t * (3 - 2 * t);
      return hz0 + (hz1 - hz0) * s;
    }
  }
  return last[1];
}

export function loadBrainwaveLab(storage = globalThis.localStorage) {
  try {
    return normalizeBrainwaveLab(JSON.parse(storage?.getItem("acelab_brainwave_lab") || "{}"));
  } catch {
    return normalizeBrainwaveLab();
  }
}

export function saveBrainwaveLab(settings, storage = globalThis.localStorage) {
  const normalized = normalizeBrainwaveLab(settings);
  try { storage?.setItem("acelab_brainwave_lab", JSON.stringify(normalized)); } catch { }
  return normalized;
}

/**
 * Independent binaural-tone laboratory. This produces actual two-frequency
 * stereo audio; headphones are required for an interaural beat.
 */
export class BrainwaveLabTone {
  constructor(context) {
    this.context = context;
    this.nodes = null;
  }

  start(settings) {
    if (this.nodes) return this.apply(settings);
    const ctx = this.context;
    if (!ctx) return false;
    const leftOsc = ctx.createOscillator();
    const rightOsc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const leftGain = ctx.createGain();
    const rightGain = ctx.createGain();
    const lfoDepthLeft = ctx.createGain();
    const lfoDepthRight = ctx.createGain();
    const leftDelay = ctx.createDelay(0.1);
    const rightDelay = ctx.createDelay(0.1);
    const merger = ctx.createChannelMerger(2);
    const dryGain = ctx.createGain();
    const echoDelay = ctx.createDelay(1);
    const echoFeedback = ctx.createGain();
    const echoWet = ctx.createGain();
    const master = ctx.createGain();
    const bassRippleGain = ctx.createGain();
    const bassLfo = ctx.createOscillator();
    const bassDepth = ctx.createGain();

    leftOsc.connect(leftGain);
    rightOsc.connect(rightGain);
    leftGain.connect(leftDelay);
    rightGain.connect(rightDelay);
    leftDelay.connect(merger, 0, 0);
    rightDelay.connect(merger, 0, 1);
    merger.connect(dryGain);
    dryGain.connect(master);
    merger.connect(echoDelay);
    echoDelay.connect(echoWet);
    echoWet.connect(master);
    echoDelay.connect(echoFeedback);
    echoFeedback.connect(echoDelay);
    master.connect(bassRippleGain);
    bassRippleGain.connect(ctx.destination);
    bassLfo.connect(bassDepth);
    bassDepth.connect(bassRippleGain.gain);
    lfo.connect(lfoDepthLeft);
    lfo.connect(lfoDepthRight);
    lfoDepthLeft.connect(leftGain.gain);
    lfoDepthRight.connect(rightGain.gain);

    leftOsc.start();
    rightOsc.start();
    lfo.start();
    bassLfo.start();
    this.nodes = {
      leftOsc, rightOsc, lfo, leftGain, rightGain, lfoDepthLeft, lfoDepthRight,
      leftDelay, rightDelay, merger, dryGain, echoDelay, echoFeedback, echoWet,
      master, bassRippleGain, bassLfo, bassDepth,
    };
    return this.apply(settings);
  }

  apply(value) {
    if (!this.nodes) return false;
    const settings = normalizeBrainwaveLab(value);
    const ctx = this.context;
    const now = ctx.currentTime;
    const nodes = this.nodes;
    nodes.leftOsc.type = settings.waveform;
    nodes.rightOsc.type = settings.waveform;
    nodes.lfo.type = settings.waveform;
    nodes.leftOsc.frequency.setTargetAtTime(settings.baseFrequencyHz, now, 0.03);
    nodes.rightOsc.frequency.setTargetAtTime(settings.baseFrequencyHz + settings.beatHz, now, 0.03);
    nodes.lfo.frequency.setTargetAtTime(settings.beatHz, now, 0.03);
    // Normalization clamps bassPulseBpm, but clamp() falls through to a default
    // only for non-finite input; guard the division anyway so a malformed scene
    // can never send NaN to an AudioParam ("non-finite" TypeError, which aborts
    // the whole apply pass).
    const bpm = Number(settings.bassPulseBpm);
    nodes.bassLfo.frequency.setTargetAtTime(
      Number.isFinite(bpm) ? Math.max(0, bpm) / 60 : 0.6, now, 0.1,
    );
    const bassRippleDepth = settings.bassRippleEnabled ? settings.bassRippleDepth : 0;
    nodes.bassRippleGain.gain.setTargetAtTime(1 - bassRippleDepth / 2, now, 0.1);
    nodes.bassDepth.gain.setTargetAtTime(bassRippleDepth / 2, now, 0.1);
    const baseGain = 0.42 * (1 - settings.modulationDepth * 0.5);
    const lfoDepth = 0.42 * settings.modulationDepth * 0.5;
    nodes.leftGain.gain.setTargetAtTime(baseGain, now, 0.03);
    nodes.rightGain.gain.setTargetAtTime(baseGain, now, 0.03);
    nodes.lfoDepthLeft.gain.setTargetAtTime(lfoDepth, now, 0.03);
    nodes.lfoDepthRight.gain.setTargetAtTime(lfoDepth, now, 0.03);
    const phaseDelayMs = settings.spatialEnabled
      ? (settings.phaseDegrees / 360) * (1000 / (settings.baseFrequencyHz + settings.beatHz))
      : 0;
    nodes.leftDelay.delayTime.setTargetAtTime(Math.max(0, -phaseDelayMs / 1000), now, 0.03);
    nodes.rightDelay.delayTime.setTargetAtTime(
      Math.max(0, ((settings.spatialEnabled ? settings.stereoDelayMs : 0) + Math.max(0, phaseDelayMs)) / 1000), now, 0.03,
    );
    nodes.echoDelay.delayTime.setTargetAtTime(settings.echoDelayMs / 1000, now, 0.03);
    nodes.echoFeedback.gain.setTargetAtTime(settings.echoEnabled ? settings.echoFeedback : 0, now, 0.03);
    nodes.echoWet.gain.setTargetAtTime(settings.echoEnabled ? settings.echoMix : 0, now, 0.03);
    nodes.dryGain.gain.setTargetAtTime(1 - (settings.echoEnabled ? settings.echoMix : 0) * 0.5, now, 0.03);
    nodes.master.gain.setTargetAtTime(
      settings.enabled && settings.mixEnabled && settings.binauralEnabled && settings.toneEnabled ? settings.volume : 0,
      now,
      0.04,
    );
    return true;
  }

  stop() {
    if (!this.nodes) return;
    const nodes = this.nodes;
    try { nodes.master.gain.setTargetAtTime(0, this.context.currentTime, 0.03); } catch { }
    try { nodes.leftOsc.stop(this.context.currentTime + 0.12); } catch { }
    try { nodes.rightOsc.stop(this.context.currentTime + 0.12); } catch { }
    try { nodes.lfo.stop(this.context.currentTime + 0.12); } catch { }
    try { nodes.bassLfo.stop(this.context.currentTime + 0.12); } catch { }
    this.nodes = null;
  }
}

export function brainwaveLabScene(scene, settings, elapsedSeconds = 0) {
  const lab = normalizeBrainwaveLab(settings);
  // Assigned experiment conditions own their parameters. The global lab is a
  // user-facing override for ordinary scenes, but it must not silently turn a
  // clean control into a different condition.
  if (!lab.enabled || scene?.condition_lock === true) return scene || {};
  // Resolve the live target: the base band arc (target depth/type) with the
  // arousal pulse overlaid (rise->hold->drop->rest). This is what makes the
  // system drive toward the target depth over time (Black Book §5.5).
  const elapsed = Math.max(0, Number(elapsedSeconds) || 0);
  const pulse = resolveArousalPulseHz(elapsed, lab);
  const liveHz = Math.max(0.5, Math.min(60, pulse.hz));
  const derivedArc = resolveTargetBandArc(lab);
  return {
    ...(scene || {}),
    binHz: liveHz,
    targetBrainwaveHz: liveHz,
    arousalPulsePhase: pulse.phase,
    beatRateTrajectoryEnabled: lab.beatRateTrajectoryEnabled,
    beatRateTrajectoryDepthHz: lab.beatRateTrajectoryDepthHz,
    beatRateTrajectoryPeriodSeconds: lab.beatRateTrajectoryPeriodSeconds,
    bpm: lab.bassPulseBpm,
    binauralEnabled: lab.mixEnabled && lab.binauralEnabled,
    // Expose the operator's carrier and level to the deck. Both were previously
    // unreachable: the deck hardcoded baseFreq = 128 and clamped binauralLevel
    // to a -48 dB ceiling, so the "Base carrier" slider did nothing and no
    // control existed for the level that determines whether the beat is audible.
    beatCarrierBaseHz: lab.baseFrequencyHz,
    binauralLevel: lab.binauralLevel,
    subliminalRippleDepth: lab.subliminalEnabled && lab.mixEnabled ? lab.subliminalRippleDepth : 0,
    subliminalBreathDepth: lab.breathEnabled ? lab.breathDepth : 0,
    breathInSeconds: lab.breathInSeconds,
    breathOutSeconds: lab.breathOutSeconds,
    breathSoundEnabled: lab.mixEnabled && lab.breathEnabled && lab.breathSoundEnabled,
    breathSoundLevel: lab.breathSoundLevel,
    breathAirColor: lab.breathAirColor,
    somaticSoundEnabled: lab.mixEnabled,
    heartbeatAudioEnabled: lab.mixEnabled && lab.heartbeatEnabled,
    heartbeatAudioLevel: lab.heartbeatLevel,
    heartbeatBpm: lab.bassPulseBpm,
    heartbeatCarrierHz: lab.heartbeatCarrierHz,
    heartbeatDoubletMs: lab.heartbeatDoubletMs,
    heartbeatHarmonicRatio: lab.heartbeatHarmonicRatio,
    brainwaveLabEnabled: true,
    brainwaveWaveform: lab.waveform,
    brainwavePhaseDegrees: lab.spatialEnabled ? lab.phaseDegrees : 0,
    brainwaveStereoDelayMs: lab.spatialEnabled ? lab.stereoDelayMs : 0,
    brainwaveCrossfeed: lab.spatialEnabled ? lab.crossfeed : 0,
    brainwaveCarrierShiftHz: lab.carrierShiftHz,
    brainwaveHighpassHz: lab.filtersEnabled ? lab.highpassHz : 20,
    brainwaveLowpassHz: lab.filtersEnabled ? lab.lowpassHz : 18000,
    subliminalLevel: lab.subliminalEnabled && lab.mixEnabled
      ? Math.min(lab.subliminalGain, SUBLIMINAL_GAIN_CEILING) : 0,
    backgroundSubliminalLevel: lab.subliminalEnabled && lab.mixEnabled
      ? Math.min(lab.backgroundGain, SUBLIMINAL_GAIN_CEILING) : 0,
    bassBrainwaveRippleEnabled: lab.mixEnabled && lab.bassRippleEnabled,
    bassBrainwaveRippleDepth: lab.mixEnabled ? lab.bassRippleDepth : 0,
    envelopeEntrainment: lab.subliminalEnabled && lab.mixEnabled ? lab.envelopeEntrainment : 0,
    envelopeAttackMs: lab.envelopeAttackMs,
    envelopeReleaseMs: lab.envelopeReleaseMs,
    covertMaskDepth: lab.subliminalEnabled && lab.mixEnabled ? lab.covertMaskDepth : 0,
    covertMaskHz: lab.covertMaskHz,
    polytemporalEnabled: lab.mixEnabled && lab.polytemporalEnabled,
    polytemporalTargetHz: liveHz,
    polytemporalLevel: lab.polytemporalLevel,
    polytemporalDepth: lab.polytemporalDepth,
    polytemporalDirectAmEnabled: lab.polytemporalDirectAmEnabled,
    polytemporalDirectAmDepth: lab.polytemporalDirectAmDepth,
    polytemporalHarmonicEnabled: lab.polytemporalHarmonicEnabled,
    polytemporalHarmonicDepth: lab.polytemporalHarmonicDepth,
    polytemporalCrossFrequencyEnabled: lab.polytemporalCrossFrequencyEnabled,
    polytemporalCrossFrequencyHz: lab.polytemporalCrossFrequencyHz,
    polytemporalCrossFrequencyDepth: lab.polytemporalCrossFrequencyDepth,
    polytemporalSpatialWidth: lab.spatialEnabled ? lab.polytemporalSpatialWidth : 0,
    polytemporalTransientLevel: lab.polytemporalTransientLevel,
    polytemporalJitterPercent: lab.polytemporalJitterPercent,
    polytemporalCarrierProfile: lab.polytemporalCarrierProfile,
    polytemporalPulseClarity: lab.polytemporalPulseClarity,
    polytemporalComfort: lab.polytemporalComfort,
    polytemporalMovement: lab.polytemporalMovement,
    polytemporalRegularity: lab.polytemporalRegularity,
    polytemporalBreathEnabled: lab.breathEnabled,
    polytemporalContinuousDeepening: lab.polytemporalContinuousDeepening,
    polytemporalDeepeningCycleSeconds: lab.polytemporalDeepeningCycleSeconds,
    polytemporalDescentBias: lab.polytemporalDescentBias,
    polytemporalBandArcEnabled: lab.polytemporalBandArcEnabled,
    polytemporalBandArc: derivedArc,
  };
}

export function resolveBrainwaveLabPlaybackSettings(scene, settings) {
  const normalized = normalizeBrainwaveLab(settings);
  if (scene?.condition_lock !== true) return normalized;
  return normalizeBrainwaveLab({
    ...normalized,
    enabled: false,
    toneEnabled: false,
    mixEnabled: false,
    spatialEnabled: false,
  });
}
